import olive from "@/assets/color-drenching/olive-green-color-drenched-paneled-room-coffered-ceiling.webp.asset.json";
import sageLib from "@/assets/color-drenching/sage-green-color-drenched-library-built-ins-ceiling.webp.asset.json";
import blackPanel from "@/assets/color-drenching/black-color-drenched-paneled-room-coffered-ceiling.webp.asset.json";
import theater from "@/assets/color-drenching/black-color-drenched-home-theater-walls-ceiling.webp.asset.json";
import sageCab from "@/assets/color-drenching/sage-green-color-drenched-built-in-cabinets-trim.webp.asset.json";
import dining from "@/assets/color-drenching/taupe-color-drenched-dining-room-paneled-walls-ceiling.webp.asset.json";
import powder from "@/assets/color-drenching/greige-color-drenched-powder-room-wall-molding-ceiling.webp.asset.json";
import paramusBedroom from "@/assets/paramus/color-drenching-primary-bedroom-tray-ceiling-paramus-nj.webp.asset.json";

export const COLOR_DRENCHING_SERVICE = "Color Drenching";
export const COLOR_DRENCHING_PATH = "/color-drenching/";
export const COLOR_DRENCHING_SMS_MESSAGE =
  "Hi All Time Paint & Plaster Co., I’m interested in color drenching. My name is ____. My project is located in ____. The room and surfaces I’d like included are ____. I’ll attach photos below.";

export interface ColorDrenchingPhoto {
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
  town?: string;
  projectHref?: string;
  projectLabel?: string;
}

/**
 * Verified completed color drenching photos only. Town, room and project
 * links appear only when confirmed — never invent details or add unrelated
 * photos. The Paramus primary bedroom is owner-confirmed color drenching.
 */
export const colorDrenchingPhotos: ColorDrenchingPhoto[] = [
  {
    src: paramusBedroom.url,
    width: 949,
    height: 1657,
    alt: "Completed color drenching in a Paramus, NJ primary bedroom with coordinated painted walls, tray ceiling and trim",
    caption: "Primary bedroom — walls, tray ceiling and trim in one coordinated color",
    town: "Paramus, NJ",
    projectHref: "/projects/luxury-new-construction-painting-paramus-nj/",
    projectLabel: "View the Paramus project",
  },
  {
    src: olive.url,
    width: 942,
    height: 1670,
    alt: "Olive green color drenching on paneled walls, wall molding, baseboards and coffered ceiling in a North Jersey home",
    caption: "Olive green paneled room — walls, molding and coffered ceiling in one color",
  },
  {
    src: sageLib.url,
    width: 1080,
    height: 1920,
    alt: "Sage green color drenched library with painted built-in bookshelves, cabinets, crown molding, wall panels and ceiling",
    caption: "Sage green library — built-ins, walls, crown and ceiling drenched in one shade",
  },
  {
    src: blackPanel.url,
    width: 1080,
    height: 1275,
    alt: "Black color drenching across paneled walls, window trim and coffered ceiling in a sunlit room",
    caption: "Black paneled room — walls, window trim and coffered ceiling",
  },
  {
    src: theater.url,
    width: 1080,
    height: 1920,
    alt: "Black color drenched home theater with painted wall panels, baseboards and ceiling around the projection screen",
    caption: "Home theater — dark walls, panels and ceiling for a cinema feel",
  },
  {
    src: sageCab.url,
    width: 1080,
    height: 1920,
    alt: "Sage green color drenched built-in cabinetry, shelving, crown molding and wall panel lit by brass picture lights",
    caption: "Sage green built-ins — cabinets, shelving and crown in one color",
  },
  {
    src: dining.url,
    width: 1080,
    height: 1920,
    alt: "Taupe color drenching on dining room wall paneling and ceiling for a soft tonal finish",
    caption: "Taupe dining room — paneled walls and ceiling in a soft tonal color",
  },
  {
    src: powder.url,
    width: 1080,
    height: 1920,
    alt: "Greige color drenched powder room with matching wall molding, crown and ceiling, fixtures protected during painting",
    caption: "Greige powder room in progress — walls, molding and ceiling in one color",
  },
];

export const colorDrenchingFaqs = [
  {
    question: "Does color drenching have to be dark?",
    answer: "No. Color drenching works with soft neutrals just as well as deeper shades. The goal is one coordinated color carried across the surfaces you choose, at whatever depth suits your room and light.",
  },
  {
    question: "Do the walls, ceiling, and trim need the same sheen?",
    answer: "Not necessarily. Different paint products and sheens can suit each surface — for example, a washable finish on walls and a harder-wearing finish on trim — while keeping the coordinated appearance.",
  },
  {
    question: "Can doors and built-ins be included?",
    answer: "Yes, when they’re part of the agreed scope. Interior doors, casings, window trim and built-ins can be included in the palette. The exact surfaces are confirmed in your estimate.",
  },
  {
    question: "Can you help me choose a color?",
    answer: "Yes. We offer color consultation as part of the estimate conversation, and custom 12×12 sample boards so you can see the color and finish in your own light before deciding.",
  },
  {
    question: "What affects the cost?",
    answer: "Room size, ceiling height, surface condition, the amount of trim and detail work, and which surfaces are included all shape the estimate. We review your room and provide a detailed written estimate.",
  },
];
